import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// D07: FK constraints added. tenantId excluded (partition key in per-tenant DB).
import { sentEmails } from "./email";

// ---------------------------------------------------------------------------
// Bounce type enum
// ---------------------------------------------------------------------------

export const BOUNCE_TYPES = ["hard", "soft"] as const;

export type BounceType = (typeof BOUNCE_TYPES)[number];

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const bounceEvents = pgTable(
  "bounce_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    correlationId: text("correlation_id")
      .notNull()
      .references(() => sentEmails.correlationId, { onDelete: "cascade" }),
    recipientEmail: text("recipient_email").notNull(),
    recipientEmailHash: text("recipient_email_hash").notNull(),
    bounceType: text("bounce_type").notNull(),
    bounceSubType: text("bounce_sub_type"),
    diagnosticCode: text("diagnostic_code"),
    backendMessageId: text("backend_message_id"),
    raw: jsonb("raw").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_bounce_events_tenant").on(table.tenantId),
    index("idx_bounce_events_tenant_recipient").on(
      table.tenantId,
      table.recipientEmailHash,
    ),
    index("idx_bounce_events_correlation").on(table.correlationId),
    index("idx_bounce_events_tenant_created").on(
      table.tenantId,
      table.createdAt,
    ),
  ],
);

export const complaintEvents = pgTable(
  "complaint_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    correlationId: text("correlation_id")
      .notNull()
      .references(() => sentEmails.correlationId, { onDelete: "cascade" }),
    recipientEmail: text("recipient_email").notNull(),
    recipientEmailHash: text("recipient_email_hash").notNull(),
    complaintType: text("complaint_type"),
    feedbackLoopSource: text("feedback_loop_source"),
    backendMessageId: text("backend_message_id"),
    raw: jsonb("raw").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_complaint_events_tenant").on(table.tenantId),
    index("idx_complaint_events_correlation").on(table.correlationId),
    index("idx_complaint_events_tenant_created").on(
      table.tenantId,
      table.createdAt,
    ),
  ],
);

export const deliveryEvents = pgTable(
  "delivery_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    correlationId: text("correlation_id")
      .notNull()
      .references(() => sentEmails.correlationId, { onDelete: "cascade" }),
    recipientEmail: text("recipient_email").notNull(),
    backendMessageId: text("backend_message_id"),
    smtpResponse: text("smtp_response"),
    processingTimeMs: integer("processing_time_ms"),
    raw: jsonb("raw").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_delivery_events_tenant").on(table.tenantId),
    index("idx_delivery_events_correlation").on(table.correlationId),
  ],
);

export const softBounceTracker = pgTable(
  "soft_bounce_tracker",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    recipientEmailHash: text("recipient_email_hash").notNull(),
    bounceCount: integer("bounce_count").notNull().default(1),
    firstBouncedAt: timestamp("first_bounced_at", tz).notNull().defaultNow(),
    lastBouncedAt: timestamp("last_bounced_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_soft_bounce_tracker_tenant_recipient").on(
      table.tenantId,
      table.recipientEmailHash,
    ),
  ],
);
